import React from 'react';
import { GenericTag } from '@/styles/GenericTag';
import { Trash2, Copy, Download, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { useProject } from '@/components/ProjectSelector';
import { BillPaymentsService, saveBillPayment, type BillPaymentInput } from '../services/billPaymentsService';
import { trackButtonClick } from '@/lib/posthog-events';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { PaymentForm } from './PaymentForm';

export function BillPaymentsTable() {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  // The list: ONE server-driven query (search + sort + page) -- the database searches, sorts and pages every payment the
  // user may see (also by the bill it pays); the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, any>({
    entityType: 'bill_payment',
    enabled: !!companyId,
    projectId: selectedProject,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'id', direction: 'asc', sortKey: 'created_at' },
    mapRow: (row) => row,
  });
  const payments = list.items;
  const { projects } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  const [currentPayment, setCurrentPayment] = React.useState<any>(null);
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showForm, setShowForm] = React.useState(false);

  const handleNewPayment = () => {
    if (!companyId || !user) return;
    setCurrentPayment(null);
    setShowForm(true);
  };

  const handleEditPayment = async (payment: any) => {
    try {
      const attachmentData = await fetchEntityAttachments('bill_payment', payment.id);
      setCurrentPayment({ ...payment, attachments: attachmentData });
      setShowForm(true);
    } catch (error) {
      console.error('Error loading payment:', error);
    }
  };

  // Creates or updates (the form sends `id` when it edits) in ONE request; files are the form's job (useAttachmentStaging).
  const handleSavePayment = async (payload: Partial<BillPaymentInput> & { id?: string }) => {
    if (!companyId || !user) return;
    return saveBillPayment(payload, { companyId: companyId as string, userId: user.id });
  };

  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one payment to copy.');
      return;
    }

    if (!user?.id) {
      alert('Please log in again to copy payments.');
      return;
    }

    try {
      const selectedPayments = payments.filter((payment) => selectedRows.includes(payment.id));
      let successCount = 0;
      let errorCount = 0;

      for (const payment of selectedPayments) {
        try {
          await BillPaymentsService.create({
            project_id: payment.project_id,
            bill_id: payment.bill_id,
            vendor_id: payment.vendor_id || null,
            payment_number: payment.payment_number || null,
            status: 'pending',
            payment_date: payment.payment_date,
            amount: payment.amount || 0,
            notes: payment.notes || null,
            client_request_id: crypto.randomUUID(),
          });
          successCount++;
        } catch (error) {
          console.error('Error copying bill payment:', error);
          errorCount++;
        }
      }

      list.reload();
      setSelectedRows([]);

      if (errorCount > 0) {
        alert(`Copied ${successCount} payment(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} payment(s).`);
      }
    } catch (error) {
      console.error('Error copying bill payments:', error);
      alert('Error copying bill payments. Please try again.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one payment to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} payment(s)? This action cannot be undone.`)) {
      return;
    }

    if (!user?.id) {
      alert('Please log in again to delete payments.');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const paymentId of selectedRows) {
        try {
          await BillPaymentsService.remove(paymentId);
          successCount++;
        } catch (error) {
          console.error('Error deleting bill payment:', error);
          errorCount++;
        }
      }

      list.reload();
      setSelectedRows([]);

      if (errorCount > 0) {
        alert(`Deleted ${successCount} payment(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully deleted ${successCount} payment(s).`);
      }
    } catch (error) {
      console.error('Error deleting bill payments:', error);
      alert('Error deleting bill payments. Please try again.');
    }
  };

  // Export to CSV: the payments selected in the table, or -- with nothing selected -- EVERY bill payment of the current
  // search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'bill-payments', 'bill payments');

  // Map payment statuses to GenericTag status types - use exact status names
  const mapPaymentStatusToTag = (status: string): 'pending' | 'posted' | 'voided' => {
    switch (status?.toLowerCase()) {
      case 'posted':
        return 'posted';
      case 'pending':
        return 'pending';
      case 'voided':
        return 'voided';
      default:
        return 'pending';
    }
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<any>[] = [
    {
      key: 'id',
      header: 'Payment ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'payment_id',
      render: (row) => <span className="font-medium">{row.payment_id || '-'}</span>,
    },
    {
      key: 'bill_id',
      header: 'Bill',
      width: '12rem',
      sortable: true,
      sortKey: 'bill',
      render: (row) => (row.bill ? `${row.bill.bill_id || '-'} - ${row.bill.name || 'Untitled Bill'}` : '—'),
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '10rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => projects.find((p) => p.id === row.project_id)?.name || 'Unknown Project',
    },
    {
      key: 'vendor_id',
      header: 'Vendor',
      width: '10rem',
      sortable: true,
      sortKey: 'vendor',
      render: (row) => row.vendor?.company_name || `${row.vendor?.first_name || ''} ${row.vendor?.last_name || ''}`.trim() || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapPaymentStatusToTag(row.status || 'pending')}
        />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '7rem',
      align: 'right' as const,
      sortable: true,
      sortKey: 'amount',
      render: (row) => `$${Number(row.amount || 0).toFixed(2)}`,
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'attachment_count',
      render: (row) => <span className="text-center block">{(row as any).attachment_count || 0}</span>,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      sortable: true,
      sortKey: 'comment_count',
      render: (row) => <span className="text-center block">{(row as any).comment_count || 0}</span>,
    },
  ];

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton
            label="New"
            onClick={() => {
              trackButtonClick('new_bill_payment_button', { context: 'cost_management' });
              handleNewPayment();
            }}
          />
          <SearchButton
            onSearch={list.setSearchInput}
            placeholder="Search"
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      {/* Generic Table */}
      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1200}
        onRowClick={handleEditPayment}
        selectable={true}
        selectedKeys={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      <PaymentForm
        key={currentPayment?.id ?? 'new'}
        payment={currentPayment}
        projects={projects}
        onClose={() => {
          setShowForm(false);
          setCurrentPayment(null);
        }}
        onSave={handleSavePayment}
        onSaved={list.reload}
        isOpen={showForm}
      />
        </div>
  );
}

export default BillPaymentsTable;
